/**
 * Generates a UUID
 */
function generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c: string): string => {
        const r: number = Math.floor(Math.random() * 16);
        const v: number = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Component types that can have generated IDs
 */
export type ComponentType = 'group' | 'page' | 'container' | 'nested-container' | 'canvas' | 'submodel' | 'field' | 'widget';

/**
 * Component metadata to track type without relying on ID prefix
 */
export interface ComponentMetadata {
    id: string;
    type: ComponentType;
}

/**
 * @param type - The type of component
 * @returns A unique ID string WITHOUT prefix
 */
export function generateComponentId(type: ComponentType): string {
    return generateUUID();
}

/**
 * Legacy: ID prefixes for different component types
 * Keep for backward compatibility only
 */
const LEGACY_ID_PREFIXES: Record<ComponentType, string> = {
    'group': 'group',
    'page': 'page',
    'container': 'container',
    'nested-container': 'nested',
    'canvas': 'canvas',
    'submodel': 'submodel',
    'field': 'field',
    'widget': 'widget'
};



/**
 * @param id - The ID to validate
 * @returns true if the ID is valid (supports both legacy and new format)
 */
export function isValidComponentId(id: string): boolean {
    // First check if it's a legacy prefixed ID
    const hasValidPrefix = Object.values(LEGACY_ID_PREFIXES).some(prefix => 
        id.startsWith(`${prefix}-`)
    );
    
    if (hasValidPrefix) {
        const parts = id.split('-');
        if (parts.length < 2) return false;
        
        const suffix = parts.slice(1).join('-');
    
        if (/^\d+$/.test(suffix)) return true;
        
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(suffix);
    }
    
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
}

/**
 * @param originalId - The original component ID
 * @returns A unique instance ID (for fields within containers)
 */
export function generateInstanceId(originalId: string): string {
    const uuid = generateUUID();
    return `${originalId}-instance-${uuid}`;
}

/**
 * Generate a component ID or use existing one from model spec
 * @param type - The type of component
 * @param modelSpecId - Optional ID from model spec
 * @returns The ID to use
 */
export function getOrGenerateComponentId(type: ComponentType, modelSpecId?: string): string {
    if (modelSpecId && (type === 'submodel' || type === 'field')) {
        return modelSpecId;
    }
    
    return generateComponentId(type);
}

/**
 * Helper to create component with metadata
 * @param type - The type of component
 * @param modelSpecId - Optional ID from model spec
 * @returns Component metadata with ID and type
 */
export function createComponentMetadata(type: ComponentType, modelSpecId?: string): ComponentMetadata {
    return {
        id: getOrGenerateComponentId(type, modelSpecId),
        type: type
    };
}